import {
  DecisionForMeeting,
  DocumentSelection,
  MeetingForm,
  meetingFormCaptions,
  meetingFormIcon,
  MeetingListItem,
  MeetingNavigationState,
  MeetingOption,
  meetingPlaceCaption,
  MeetingType,
  meetingTypeCaption,
  meetingTypeToDocumentTypes,
} from "@/meetings/meetingsDomain";
import { IconName } from "@/components-v2/iconMapping";
import { TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import { AppBarData } from "@/appBar/appBarDomain";
import { DropdownMenuItems, DropdownMenuProps } from "@/components-v2/Dropdown";
import { LocaleFormat } from "@/i18n/localeFormat";
import {
  atMonth,
  atTime,
  DateValue,
  isAfter,
  isBefore,
  isSameMonth,
  isSameYear,
  parseDateInput,
  plusMonths,
  plusYears,
  toDate,
  today,
  toLocalDateISOString,
  toYearISOString,
  toYearMonthISOString,
} from "@/utils/Dates";
import { Company } from "@/types/CompanyInfo";
import { ChangeEvent } from "react";
import {
  compact,
  identity,
  isEmpty,
  max,
  min,
  orderBy,
  range,
  reject,
  uniq,
  without,
} from "lodash";
import { Language } from "@/i18n/languages";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import {
  DecisionItem,
  DetailsPanelRef,
  detailsPanelToRef,
  getDecisionAttachmentsInDocuments,
  getDecisionIcon,
  getDecisionName,
  getDocumentStatus,
  getMatterTypeLabel,
  isInformationMatterInDocument,
  ProjectJsonData,
  removeDocumentAction,
} from "@/dbot/common/commonDomain";
import { when } from "@/utils/functions";
import { withoutProtocol } from "@/utils/urls";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import {
  findDateNoticeGeneralMeeting,
  findNoticeToGeneralMeetingForm,
  isDecisionExcludedInDocument,
  ProjectController,
  TabInPreview,
} from "@/dbot/project/projectDomain";
import AlertMessageStore, { AlertMessage } from "@/stores/AlertMessageStore";
import {
  actionName,
  DCEvent,
  EventDispatcher,
  HttpEvent,
  HttpResponse,
  loading,
  Loading,
  navigate,
  NavigateEvent,
  showAlert,
  showGeneralError,
  updateStore,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { mapBoardMeetingFormToMeetingForm } from "@/dbot/noticeBoardMeeting/noticeBoardMeetingDomain";
import {
  adminBeneficialOwners,
  adminShareholderStatement,
  boardMemberBoardMeetingPage,
  navigateToProjectEvent,
  previewBoardMeetingForAdmin,
  previewGeneralMeetingForAdmin,
  resolveNoticeForShareholderUrl,
  ScheduleRouteParams,
  scheduleUrl,
} from "@/utils/navigation";
import { TabProps } from "@/components-v2/Tabs";
import { LinkButtonProps, ToggleButtonProps } from "@/components-v2/Button";
import { Calendar, CalendarDay, makeCalendar } from "@/calendar/calendar";
import { SelectProps } from "@/components-v2/Select";
import { InfoTagDetailsItemProps, InfoTagProps } from "@/components-v2/InfoTag";
import { AgendaItemCardProps } from "@/meetings/meetingsComponents";
import { LocalizedText } from "@/common/common";
import {
  CommentDTO,
  CommentListProps,
  commentsApi,
  EditCommentState,
} from "@/comments/commentsDomain";
import { getNoticeToGeneralMeetingDetailsPanel } from "@/dbot/noticeGeneralMeeting/noticeGeneralMeetingDomain";
import { mapGeneralMeetingFormToMeetingForm } from "@/dbot/common/commonGeneralMeetingDomain";
import { TagProps } from "@/components-v2/Tag";
import { IconColor } from "@/components-v2/Icon";
import { AccessType } from "@/types/enums/AccessType";
import { AddToCalendarProps, DownloadAsPdfButtonProps } from "@/common/commonComponents";
import { downloadEvent } from "@/api/DownloadService";
import { DecisionType } from "@/common/decisions";
import { RootStoreData } from "@/events/rootStoreData";
import { updateUserPrefEvent, UserPrefs } from "@/stores/userPrefsDomain";
import {
  openSigningStatusDialogAction,
  openViewingStatusDialog,
} from "@/dbot/summary/summaryDomain";
import { DocumentSigningStatus } from "@/document/documentApi";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { logDebug } from "@/utils/log";
import { toJS } from "mobx";
import { CreateMeetingRequest, projectApi } from "@/dbot/common/projectApi";

export type ScheduleMeeting = {
  type: "meeting";
  id: string;
  date: string;
  time?: string;
  endTime?: string;
  meeting: MeetingListItem;
};

export type ScheduleMeetingReminder = {
  type: "meeting-reminder";
  id: string;
  date: string;
  time?: string;
  endTime?: string;
  meeting: MeetingListItem;
  isDone?: boolean;
};

export type ShareholderRegisterStatementReminder = {
  type: "shareholder-register-statement-reminder";
  id: string;
  date: string;
  reportingYear: number;
  time?: string;
  isDone?: boolean;
};

export type BeneficialOwnersReminder = {
  type: "beneficial-owners-reminder";
  id: string;
  date: string;
  time?: string;
  isDone?: boolean;
};

export type ScheduleItem =
  | ScheduleMeeting
  | ScheduleMeetingReminder
  | ShareholderRegisterStatementReminder
  | BeneficialOwnersReminder;

export function isMeetingType(
  item: ScheduleItem | undefined,
): item is ScheduleMeeting | ScheduleMeetingReminder {
  return item?.type === "meeting" || item?.type === "meeting-reminder";
}

export type ScheduleItemType = MeetingType;

export type SchedulePeriodType = "year" | "month";

export type SchedulePeriod = { type: SchedulePeriodType; value: DateValue };

export type SelectedItem = {
  id: string;
  projectId?: string;
  ctrl?: ProjectController;
  activeTab?: string;
  cleanup?: () => void;
  isDescriptionEnabled?: boolean;
  selectedAgendaItem?: string;
  isLoadingComments?: boolean;
  comments?: CommentDTO[];
  editComment?: EditCommentState;
  itemDetails?: GetScheduleItemDetailsResponse;
};

export type ScheduleState = {
  accessType: AccessType;
  isLoading?: boolean;
  items: ScheduleItem[];
  itemTypes: ScheduleItemType[];
  orgNo?: string;
  listPeriod: SchedulePeriod;
  calendarPeriod: SchedulePeriod;
  selectedItem?: SelectedItem;
  view: "list" | "calendar";
  shareholderId?: number;
};

export type ScheduleItemStatus =
  | { key: string; label: TMessage | TMessageParam; onOpenDetails?: () => void }
  | { key: string; tag: TagProps };

export type ScheduleItemProps = {
  key: string;
  icon: IconName;
  iconColor?: IconColor;
  date?: string;
  time?: string;
  title: TMessage;
  description?: string;
  company?: string;
  tags?: InfoTagProps[];
  statuses?: ScheduleItemStatus[];
  isSelected?: boolean;
  onSelect?: DCEvent[];
  compact?: boolean;
  statusTag?: TagProps;
};

export type ToolbarButton =
  | {
      key: string;
      variant: "icon";
      icon: IconName;
      tooltip: TMessage;
      color?: "inherit";
      onClick: (() => void) | DCEvent[];
    }
  | {
      key: string;
      variant: "primary" | "secondary" | "basic";
      icon?: IconName;
      label?: TMessage;
      tooltip?: TMessage;
      onClick: (() => void) | DCEvent[];
    };

export type MeetingDetailsProps = {
  type: "meeting-details";
  meetingType: MeetingType;
  title: TMessage;
  date: string;
  toolbar: ToolbarButton[];
  tabs: TabProps[];
  activeTab: string;
  activeTabContent?: TabInPreview["content"];
  activeTabAttachments?: UploadedAttachment[];
  ctrl?: ProjectController;
  openAttachment: (attachmentId: string, attachments: UploadedAttachment[]) => DCEvent[];
  removeDocument?: (documentId: string) => void;
  editAllSettings?: () => void;
  isDescriptionEnabled?: boolean;
  enableDescription?: DCEvent[];
  agendaItems?: AgendaItemCardProps[];
  selectedAgendaItem?: AgendaItemCardProps;
  comments?: CommentListProps;
  downloadDocument?: (content: { documentId: string }) => DownloadAsPdfButtonProps | undefined;
  showDocumentStatus?: LinkButtonProps;
  isPlaceholder?: boolean;
  showPlaceholderSettings?: boolean;
  openProject?: DCEvent[];
};

export type MeetingReminderDetailsProps = {
  type: "meeting-reminder-details";
  date: DateValue;
  title: TMessage;
  status: "not-completed" | "completed" | "overdue";
  completedDate?: DateValue;
  openProject?: DCEvent[];
  close: DCEvent[];
};

export type MeetingPlaceholderDetailsProps = {
  type: "meeting-placeholder-details";
  addToCalendar?: AddToCalendarProps;
  close: DCEvent[];
  date: DateValue;
  description?: string;
  endTime?: string;
  helpText?: TMessage;
  infoTags?: InfoTagProps[];
  infoTagDetails?: InfoTagDetailsItemProps[];
  startTime?: string;
  title: TMessage;
};

export type ShareholderRegisterStatementDetailsProps = {
  type: "shareholder-register-statement-reminder-details";
  title: TMessage;
  date: DateValue;
  reportingYear: number;
  status: "not-completed" | "completed" | "overdue";
  close: DCEvent[];
  openShareholderRegisterStatement: DCEvent[];
};

export type BeneficialOwnersReminderDetailsProps = {
  type: "beneficial-owners-reminder-details";
  title: TMessage;
  date: DateValue;
  status: "not-completed" | "completed" | "overdue";
  close: DCEvent[];
  openBeneficialOwnersPage: DCEvent[];
};

export type ScheduleItemDetailsProps =
  | Loading
  | MeetingDetailsProps
  | MeetingReminderDetailsProps
  | MeetingPlaceholderDetailsProps
  | ShareholderRegisterStatementDetailsProps
  | BeneficialOwnersReminderDetailsProps;

export type ScheduleViewProps = {
  appBar?: AppBarData | null;
  alertStore: AlertMessageStore;
  addLabel: TMessage;
  addItems?: DropdownMenuProps["items"];
  itemTypes: { id: ScheduleItemType; label: TMessage; selected: boolean }[];
  onNextPeriod: DCEvent[];
  onPrevPeriod: DCEvent[];
  onToday: DCEvent[];
  onToggleItemType: (
    event: ChangeEvent<HTMLInputElement>,
    option: { id: ScheduleItemType; label: TMessage; selected: boolean },
    checked: boolean,
  ) => DCEvent[];
  periodSelector: SelectProps<DateValue>;
  periodTypeSelector?: SelectProps<SchedulePeriodType>;
  selectedItem?: ScheduleItemDetailsProps;
  sortOrder?: { label: TMessage; icon: IconName; onClick: DCEvent[] };
  viewOptions: (ToggleButtonProps & { onClick: DCEvent[] })[];
  view:
    | {
        type: "list";
        emptyState?: EmptyStateProps;
        items: ScheduleItemProps[];
        isLoading?: boolean;
      }
    | {
        type: "calendar";
        addItems?: (day: CalendarDay<ScheduleItemProps>) => DropdownMenuProps["items"];
        calendar: Calendar<ScheduleItemProps>;
        isLoading?: boolean;
      };
};

export type GetScheduleResponse = {
  items: ScheduleItem[];
};

type NewScheduleItem = { type: "meeting"; data: CreateMeetingRequest };

export type NewScheduleItemResponse = { id: string; createdItems: ScheduleItem[] };

type GetScheduleRequest = {
  orgNo: string;
  shareholderId?: number;
  onSuccess: (res: HttpResponse<GetScheduleResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

export type GetScheduleItemDetailsRequest = {
  orgNo: string;
  id: string;
  onSuccess: (res: HttpResponse<GetScheduleItemDetailsResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

export type GetScheduleItemDetailsResponse = GetMeetingDetailsResponse;

type MeetingDocument = {
  id: string;
  type: DocumentType;
  content?: string;
  attachments?: UploadedAttachment[];
};

export type GetMeetingDetailsResponse = {
  type: "meeting-details";
  notice?: MeetingDocument;
  minutes?: MeetingDocument;
};

export type AdminScheduleApi = {
  getSchedule: ({ orgNo, onSuccess, onFailure }: GetScheduleRequest) => HttpEvent;
  newItem: ({
    orgNo,
    payload,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    payload: NewScheduleItem;
    onSuccess: HttpEvent<NewScheduleItemResponse>["onSuccess"];
    onFailure: HttpEvent["onFailure"];
  }) => HttpEvent;
};

export const adminScheduleApi: AdminScheduleApi = {
  getSchedule: ({ orgNo, onSuccess, onFailure }): HttpEvent => ({
    type: "http",
    method: "get",
    url: `/schedule/${orgNo}`,
    onSuccess,
    onFailure,
  }),

  newItem: ({ orgNo, payload, onSuccess, onFailure }): HttpEvent => ({
    type: "http",
    method: "post",
    url: `/schedule/${orgNo}`,
    data: payload,
    onSuccess,
    onFailure,
  }),
};

export type BoardMemberScheduleApi = {
  getSchedule: ({ orgNo, onSuccess, onFailure }: GetScheduleRequest) => HttpEvent;
  getItemDetails: ({
    orgNo,
    id,
    onSuccess,
    onFailure,
  }: GetScheduleItemDetailsRequest) => HttpEvent<GetScheduleItemDetailsResponse>;
};

export const boardMemberScheduleApi: BoardMemberScheduleApi = {
  getSchedule: ({ orgNo, onSuccess, onFailure }): HttpEvent => ({
    type: "http",
    method: "get",
    url: `/board-member/schedule/${orgNo}`,
    onSuccess,
    onFailure,
  }),

  getItemDetails: ({
    orgNo,
    id,
    onSuccess,
    onFailure,
  }): HttpEvent<GetScheduleItemDetailsResponse> => ({
    type: "http",
    method: "get",
    url: `/board-member/schedule/${orgNo}/${id}`,
    onSuccess,
    onFailure,
  }),
};

export type ShareholderScheduleApi = {
  getSchedule: ({ orgNo, onSuccess, onFailure }: GetScheduleRequest) => HttpEvent;
};

export const shareholderScheduleApi: ShareholderScheduleApi = {
  getSchedule: ({ orgNo, shareholderId, onSuccess, onFailure }): HttpEvent => ({
    type: "http",
    method: "get",
    url: `/shareholder/schedule/${orgNo}/${shareholderId}`,
    onSuccess,
    onFailure,
  }),
};

export const pendingId = "pending";

export const getPeriod = (state: ScheduleState = defaultScheduleState()): SchedulePeriod =>
  state.view === "list" ? state.listPeriod : state.calendarPeriod;
export const getPeriodType = (state?: ScheduleState): SchedulePeriodType => getPeriod(state).type;
export const getPeriodValue = (state?: ScheduleState): DateValue => getPeriod(state).value;

const setInScheduleState = <K extends keyof ScheduleState>(
  key: K,
  val: ScheduleState[K],
): UpdateStoreEvent =>
  updateStore({
    schedule: (schedule = defaultScheduleState()) => ({ ...schedule, [key]: val }),
  });

const updateInScheduleState = <K extends keyof ScheduleState>(
  key: K,
  val: (cur: ScheduleState[K]) => ScheduleState[K],
): UpdateStoreEvent =>
  updateStore({
    schedule: (schedule = defaultScheduleState()) => ({ ...schedule, [key]: val(schedule[key]) }),
  });

const withScheduleState = (f: (v: ScheduleState) => DCEvent[]): DCEvent => ({
  type: "with-store",
  events: (store) => f(store.schedule ?? defaultScheduleState()),
});

const sortOrderOrDefault = (userPrefs: UserPrefs["schedule"]): "asc" | "desc" =>
  userPrefs?.sortOrder ?? "desc";

const sortItems = (items: ScheduleItem[], order: "asc" | "desc"): ScheduleItem[] =>
  orderBy(items, [(x) => atTime(x.date, x.time ?? "00:00").getTime()], [order]);

export const filterItems = (state: ScheduleState): ScheduleItem[] => {
  return state.items.filter(
    (item) =>
      item.date &&
      (getPeriodType(state) === "year"
        ? isSameYear(item.date, getPeriodValue(state))
        : isSameMonth(item.date, getPeriodValue(state))) &&
      (!isMeetingType(item) || state.itemTypes.includes(item.meeting.meetingType)),
  );
};

export const openItemDetails = (
  state: ScheduleState,
  itemId: string,
  activeTab?: string,
): DCEvent[] | undefined => {
  switch (state.accessType) {
    case AccessType.SHAREHOLDER: {
      const item = state.items.filter(isMeetingType).find((x) => x.id === itemId);
      if (!item) {
        return [];
      }
      return [
        {
          type: "load-url",
          url: resolveNoticeForShareholderUrl(
            item.meeting.documents.find(
              (d) => d.documentType === DocumentType.notice_general_meeting,
            )?.documentId ?? "n/a",
          ),
          newWindow: true,
        },
      ];
    }
    case AccessType.ADMIN:
    case AccessType.BOARD_MEMBER:
      return [
        {
          type: "navigate",
          url: toScheduleUrl({
            ...state,
            selectedItem: { id: itemId, activeTab: activeTab ?? state.selectedItem?.activeTab },
          }),
        },
      ];
  }
};

export const closeItemDetails = (state: ScheduleState): DCEvent =>
  navigate(toScheduleUrl({ ...state, selectedItem: undefined }));

const showMeetingPlaceholderSettings = (item: ScheduleItem): boolean =>
  item.type === "meeting" &&
  ((item.meeting.meetingType === MeetingType.BOARD_MEETING &&
    !item.meeting.hasSentNoticeOfBoardMeeting) ||
    (item.meeting.meetingType === MeetingType.GENERAL_MEETING &&
      !item.meeting.hasSentNoticeOfGeneralMeeting));

export const isMeetingPlaceholder = (item: ScheduleItem): boolean | undefined =>
  item.type === "meeting" && item.meeting.isPlaceholder && !item.meeting.decisions.length;

const generateMeetingProjectName =
  ({ t }: { t: TFunction }, item: ScheduleMeeting | ScheduleMeetingReminder) =>
  (jsonData: ProjectJsonData) =>
    makeMeetingProjectName({
      t,
      meetingType: item.meeting.meetingType,
      meetingStartDate:
        item.meeting.meetingType === MeetingType.GENERAL_MEETING
          ? jsonData.generalMeeting?.meetingStartDate
          : jsonData.boardMeeting?.meetingStartDate,
    });

export const initItemDetails = (
  context: { t: TFunction },
  state: ScheduleState,
  params: Pick<ScheduleRouteParams, "selectedItem" | "activeTab">,
): DCEvent[] => {
  const item = state.items.find((x) => x.id === params.selectedItem);
  let loadCommentsEvents: DCEvent[] = [];
  if (isMeetingType(item) && item?.meeting.projectId && item.meeting.projectId !== pendingId) {
    if (state.selectedItem?.selectedAgendaItem) {
      loadCommentsEvents = loadCommentsForDecision(
        item?.meeting.projectId,
        state.selectedItem?.selectedAgendaItem,
      );
    } else {
      loadCommentsEvents = loadComments(item.meeting.projectId);
    }
  }
  return compact([
    updateInScheduleState("selectedItem", (selectedItem) => {
      if (item?.id === selectedItem?.id) {
        return selectedItem ? { ...selectedItem, activeTab: params.activeTab } : undefined;
      } else {
        selectedItem?.cleanup?.();
        return isMeetingType(item)
          ? { id: item.id, projectId: item.meeting.projectId, activeTab: params.activeTab }
          : item
            ? { id: item.id }
            : undefined;
      }
    }),
    state.accessType === AccessType.ADMIN &&
      isMeetingType(item) &&
      item?.meeting.projectId &&
      item.id !== state.selectedItem?.id &&
      item.id !== pendingId && {
        type: "load-project",
        projectId: item.meeting.projectId,
        onSuccess: (ctrl) => {
          const cleanup = ctrl.init({ nameGenerator: generateMeetingProjectName(context, item) });
          setupSyncOnChange({ id: item.id, ctrl });
          return compact([
            updateInScheduleState("selectedItem", (selectedItem) =>
              selectedItem?.id === item.id
                ? { ...selectedItem, id: item.id, ctrl, cleanup, activeTab: params.activeTab }
                : selectedItem,
            ),
          ]);
        },
        onFailure: () => [showGeneralError()],
      },
    state.accessType === AccessType.BOARD_MEMBER &&
      item &&
      item.id !== state.selectedItem?.id &&
      !isMeetingPlaceholder(item) &&
      boardMemberScheduleApi.getItemDetails({
        orgNo: state.orgNo ?? "",
        id: item.id,
        onSuccess: (res) => [
          updateInScheduleState("selectedItem", (selectedItem) =>
            selectedItem?.id === item.id
              ? { ...selectedItem, itemDetails: res.data }
              : selectedItem,
          ),
        ],
        onFailure: () => [closeItemDetails(state), showGeneralError()],
      }),
    ...loadCommentsEvents,
  ]);
};

export const firstPendingInPeriod = (
  state: ScheduleState,
  { currentDate }: { currentDate: DateValue },
): string | undefined =>
  sortItems(filterItems(state), "asc").find((x) => x.date && !isBefore(x.date, currentDate))?.id;

const isShareholderView = ({ accessType }: { accessType: AccessType }) =>
  accessType === AccessType.SHAREHOLDER || accessType === AccessType.ADDITIONAL_REPRESENTATIVE;

export const initSchedulePage = (
  params: ScheduleRouteParams,
  state: ScheduleState | undefined,
  { currentDate, t }: { currentDate: DateValue; t: TFunction },
): DCEvent[] => {
  if (state?.orgNo === params.orgNo && state?.accessType === params.accessType) {
    return compact([
      actionName("updateSchedulePage"),
      setInScheduleState("view", toView(params.view)),
      setInScheduleState(
        toView(params.view) === "list" ? "listPeriod" : "calendarPeriod",
        toPeriod(state, params),
      ),
      ...initItemDetails({ t }, state, params),
    ]);
  }
  const api =
    params.accessType === AccessType.BOARD_MEMBER
      ? boardMemberScheduleApi
      : isShareholderView(params)
        ? shareholderScheduleApi
        : adminScheduleApi;
  return [
    actionName("initSchedulePage"),
    updateStore({
      schedule: defaultScheduleState({
        accessType: params.accessType,
        orgNo: params.orgNo,
        isLoading: true,
        period: toPeriod(state, params),
        view: toView(params.view),
        shareholderId: params.shareholderId,
      }),
    }),
    api.getSchedule({
      orgNo: params.orgNo,
      shareholderId: params.shareholderId,
      onSuccess: (res, state) =>
        state.schedule?.orgNo !== params.orgNo || state.schedule?.accessType !== params.accessType
          ? []
          : [
              actionName("getScheduleSuccess"),
              setInScheduleState("isLoading", undefined),
              setInScheduleState("items", res.data.items),
              withScheduleState((state) =>
                initItemDetails({ t }, state, {
                  ...params,
                  selectedItem: isShareholderView(params)
                    ? undefined
                    : (params.selectedItem ?? firstPendingInPeriod(state, { currentDate })),
                }),
              ),
            ],
      onFailure: (_, state) =>
        state.schedule?.orgNo !== params.orgNo
          ? []
          : [
              actionName("getScheduleFailure"),
              showGeneralError(),
              setInScheduleState("isLoading", undefined),
              setInScheduleState("items", []),
            ],
    }),
  ];
};

export const cleanupSchedulePage = (): DCEvent[] => [
  updateStore({
    schedule: (schedule) => {
      schedule?.selectedItem?.cleanup?.();
      return undefined;
    },
  }),
];

const defaultPeriod = (): SchedulePeriod => ({
  type: "year",
  value: toLocalDateISOString(Date.now()),
});

export const toPeriod = (
  state: ScheduleState | undefined,
  { year, month }: ScheduleRouteParams,
): SchedulePeriod =>
  when(year && month && parseDateInput(`${year}-${month}`).parsed, (date) => ({
    type: "month",
    value: toLocalDateISOString(date),
  })) ??
  when(year && parseDateInput(year).parsed, (date) => ({
    type: "year",
    value:
      state && isSameYear(getPeriodValue(state), date)
        ? getPeriodValue(state)
        : toLocalDateISOString(date),
  })) ??
  defaultPeriod();

export const defaultScheduleState = ({
  accessType,
  orgNo,
  isLoading,
  period = defaultPeriod(),
  view = "list",
  items = [],
  itemTypes = Object.values(MeetingType),
  shareholderId,
}: {
  accessType?: AccessType;
  orgNo?: string;
  isLoading?: boolean;
  period?: SchedulePeriod;
  view?: "list" | "calendar";
  items?: ScheduleItem[];
  itemTypes?: ScheduleItemType[];
  shareholderId?: number;
} = {}): ScheduleState => ({
  accessType: accessType ?? AccessType.NONE,
  isLoading,
  items,
  itemTypes,
  orgNo,
  listPeriod: period,
  calendarPeriod: period,
  view,
  shareholderId,
});

const getTitlesForDecision = (d: DecisionItem): LocalizedText[] | undefined =>
  d.decisionData?.type === DecisionType.freetext ? d.decisionData.localizedTitles : undefined;

const toDecisionForMeeting = (d: DecisionItem, meetingType: MeetingType): DecisionForMeeting => {
  const documentType = meetingTypeToNoticeType(meetingType);
  return {
    decisionId: d.decisionId,
    titles: getTitlesForDecision(d),
    attachments: getAttachmentsForDecision(d, meetingType),
    decisionType: d.type,
    isInformational: isInformationMatterInDocument(d.decisionData, documentType),
    descriptions: d.decisionTexts.filter((x) => x.documentType === documentType),
  };
};

const syncChanges = (
  x: ScheduleMeeting | ScheduleMeetingReminder,
  ctrl: ProjectController,
): ScheduleItem => {
  const meetingType = x.meeting.meetingType;
  const decisions = getDecisionsForMeeting(ctrl.projectContext, meetingType).map((d) =>
    toDecisionForMeeting(d, meetingType),
  );
  switch (meetingType) {
    case MeetingType.BOARD_MEETING:
      return {
        ...x,
        date: ctrl.boardMeetingFields.meetingStartDate.validatedValue ?? x.date,
        time: ctrl.boardMeetingFields.meetingStartTime.validatedValue,
        endTime: ctrl.boardMeetingFields.meetingEndTime.validatedValue,
        meeting: {
          ...x.meeting,
          meetingStartDate: ctrl.boardMeetingFields.meetingStartDate.validatedValue,
          meetingStartTime: ctrl.boardMeetingFields.meetingStartTime.validatedValue,
          meetingForm: mapBoardMeetingFormToMeetingForm(
            ctrl.boardMeetingFields.boardMeetingForm.value,
          ),
          meetingNumber: ctrl.boardMeetingFields.boardMeetingNumber.validatedValue,
          meetingDescription: ctrl.boardMeetingFields.noticeDescription.localizedTexts,
          decisions,
        },
      };
    case MeetingType.GENERAL_MEETING:
      return {
        ...x,
        date: ctrl.generalMeetingFields.dateGeneralMeeting.validatedValue ?? x.date,
        time: ctrl.generalMeetingFields.timeGeneralMeeting.validatedValue,
        meeting: {
          ...x.meeting,
          meetingStartDate: ctrl.generalMeetingFields.dateGeneralMeeting.validatedValue,
          meetingStartTime: ctrl.generalMeetingFields.timeGeneralMeeting.validatedValue,
          meetingForm: mapGeneralMeetingFormToMeetingForm(
            ctrl.generalMeetingFields.meetingForm.value,
          ),
          meetingDescription: findNoticeToGeneralMeetingForm(ctrl.documents)
            ?.additionalDetailsTextModel.localizedTexts,
          decisions,
        },
      };
  }
};

const syncReminder = (
  x: ScheduleMeetingReminder,
  ctrl: ProjectController,
): ScheduleMeetingReminder => ({
  ...x,
  date: findDateNoticeGeneralMeeting(ctrl.documents) ?? x.date,
});

const setupSyncOnChange = ({ id, ctrl }: { id: string; ctrl: ProjectController }) => {
  ctrl.addEventListener((event) => {
    if (event.type === "dbot/save-project-scheduled") {
      ctrl.dispatch([
        updateInScheduleState("items", (items) =>
          items.map((x) =>
            isMeetingType(x) && x.id === id
              ? syncChanges(x, ctrl)
              : x.type === "meeting-reminder" && x.meeting.id === id
                ? syncReminder(x, ctrl)
                : x,
          ),
        ),
      ]);
    }
  });
};

export const makePendingMeetingItem = ({
  meetingStartDate,
  meetingStartTime,
  projectName,
  meetingType,
}: {
  meetingStartDate: string;
  meetingStartTime: string;
  projectName: string;
  meetingType: MeetingType;
}): ScheduleMeeting => ({
  type: "meeting",
  id: pendingId,
  date: meetingStartDate,
  time: meetingStartTime,
  meeting: {
    id: pendingId,
    projectId: pendingId,
    projectName,
    decisions: [],
    documents: [],
    hasSentNoticeOfBoardMeeting: false,
    hasSentNoticeOfGeneralMeeting: false,
    meetingForm: MeetingForm.PHYSICAL,
    meetingStartDate: meetingStartDate,
    meetingStartTime: meetingStartTime,
    meetingType,
  },
});

export const makeMeetingProjectName = ({
  t,
  meetingType,
  meetingStartDate,
  isExtraordinary,
}: {
  t: TFunction;
  meetingType: MeetingType;
  meetingStartDate?: string;
  isExtraordinary?: boolean;
}): string =>
  compact([
    meetingStartDate && toLocalDateISOString(meetingStartDate),
    t(meetingTypeCaption({ meetingType, isExtraordinary })),
  ]).join(" ");

export const toNewMeetingPayload = (
  state: ScheduleState,
  params: {
    date: DateValue;
    meetingType: MeetingType;
    projectName: string;
    isPastMeeting: boolean;
    ordinaryGeneralMeeting?: boolean;
  },
): CreateMeetingRequest => {
  const orgNo = state.orgNo ?? "";
  const meetingStartDate = toLocalDateISOString(params.date);
  const meetingStartTime = "12:00";
  const meetingDocumentSelection: DocumentSelection | undefined =
    params.meetingType === MeetingType.BOARD_MEETING
      ? params.isPastMeeting
        ? DocumentSelection.MINUTES_ONLY
        : DocumentSelection.NOTICE_AND_MINUTES
      : undefined;
  const meetingOptions: MeetingOption[] | undefined =
    params.meetingType === MeetingType.GENERAL_MEETING
      ? compact([!params.isPastMeeting && MeetingOption.NOTICE, MeetingOption.MINUTES])
      : undefined;
  return {
    orgNo,
    projectName: params.projectName,
    meetingType: params.meetingType,
    meetingStartDate: `${meetingStartDate}T${meetingStartTime}`,
    meetingDocumentSelection,
    meetingOptions,
    ordinaryGeneralMeeting: params.ordinaryGeneralMeeting,
    isGeneratedName: true,
  };
};

export const newMeeting = (
  state: ScheduleState,
  context: {
    t: TFunction;
    currentDate: DateValue;
  },
  params: {
    date: DateValue;
    meetingType: MeetingType;
    ordinaryGeneralMeeting?: boolean;
  },
): DCEvent[] => {
  const meetingStartDate = toLocalDateISOString(params.date);
  const meetingStartTime = "12:00";
  const projectName = makeMeetingProjectName({
    t: context.t,
    meetingType: params.meetingType,
    meetingStartDate,
  });
  const isPastMeeting = isBefore(meetingStartDate, context.currentDate);
  const pendingItem = makePendingMeetingItem({
    meetingStartDate,
    meetingStartTime,
    projectName,
    meetingType: params.meetingType,
  });
  return [
    setInScheduleState("items", [pendingItem, ...state.items]),
    ...(openItemDetails(state, pendingId, "quick-edit") ?? []),
    adminScheduleApi.newItem({
      orgNo: state.orgNo ?? "",
      payload: {
        type: "meeting",
        data: toNewMeetingPayload(state, { ...params, projectName, isPastMeeting }),
      },
      onSuccess: (res, state) => [
        setInScheduleState("items", [
          ...state.schedule!.items.filter((x) => x.id !== pendingItem.id),
          ...res.data.createdItems,
        ]),
        ...(openItemDetails(
          state.schedule!,
          res.data.id,
          !isPastMeeting ? "quick-edit" : undefined,
        ) ?? []),
      ],
      onFailure: () => [
        updateInScheduleState("items", (items) => items.filter((item) => item.id !== pendingId)),
      ],
    }),
  ];
};

const newMeetingMenuItems = (
  state: ScheduleState,
  context: { t: TFunction; currentDate: DateValue },
  params: { date: DateValue },
): DropdownMenuItems => [
  {
    type: "item",
    label: { key: "meetings.new-board-meeting" },
    onSelect: newMeeting(state, context, { ...params, meetingType: MeetingType.BOARD_MEETING }),
  },
  {
    type: "item",
    label: { key: "meetings.new-ordinary-general-meeting" },
    onSelect: newMeeting(state, context, {
      ...params,
      ordinaryGeneralMeeting: true,
      meetingType: MeetingType.GENERAL_MEETING,
    }),
  },
  {
    type: "item",
    label: { key: "meetings.new-extraordinary-general-meeting" },
    onSelect: newMeeting(state, context, { ...params, meetingType: MeetingType.GENERAL_MEETING }),
  },
];

export const prepItemStatus = (item: ScheduleMeeting): ScheduleItemStatus[] => {
  const notice = item.meeting.documents.find(
    (d) =>
      d.documentType ===
      (item.meeting.meetingType === MeetingType.BOARD_MEETING
        ? DocumentType.notice_board_meeting
        : DocumentType.notice_general_meeting),
  );
  const minutes = item.meeting.documents.find(
    (d) =>
      d.documentType ===
      (item.meeting.meetingType === MeetingType.BOARD_MEETING
        ? DocumentType.board_meeting
        : DocumentType.general_meeting),
  );

  switch (true) {
    case minutes?.signedAt !== undefined:
      return [
        {
          key: "minutes-signed-tag",
          tag: { color: "success", label: { key: "signed" } },
        },
        {
          key: "minutes-signed-extra",
          label: {
            type: "datetime",
            value: minutes.signedAt,
            relative: true,
          },
        },
      ];
    case minutes?.sentAt !== undefined &&
      minutes?.signingStatus === DocumentSigningStatus.SENT_FOR_REVIEW:
      return [
        { key: "minutes-sent-tag", tag: { color: "info", label: { key: "sent-for-review" } } },
        {
          key: "minutes-sent-extra",
          label: {
            type: "datetime",
            value: minutes.sentAt,
            relative: true,
          },
        },
      ];
    case minutes?.sentAt !== undefined:
      return [
        { key: "minutes-sent-tag", tag: { color: "warning", label: { key: "sent-for-signing" } } },
        {
          key: "minutes-sent-extra",
          label: {
            type: "datetime",
            value: minutes.sentAt,
            relative: true,
          },
        },
      ];
    case notice?.sentAt !== undefined:
      return [
        { key: "notice-sent-tag", tag: { color: "info", label: { key: "schedule.notice-sent" } } },
        {
          key: "notice-sent-extra",
          label: { type: "datetime", value: notice.sentAt, relative: true },
        },
      ];
    case notice !== undefined:
      return [
        {
          key: "notice-not-sent-tag",
          tag: { color: "neutral", label: { key: "schedule.notice-not-sent" } },
        },
      ];
    default:
      return [];
  }
};

export const findLocalizedTextOrDefault = (
  { lang }: { lang: Language },
  texts: LocalizedText[] | undefined,
): string | undefined => texts?.find((d) => d.language === lang)?.text ?? texts?.[0]?.text;

const findMeetingDescription = ({ lang }: { lang: Language }, m: MeetingListItem) =>
  when(m.meetingDescription, (texts) => findLocalizedTextOrDefault({ lang }, texts));

export const getMeetingFormInfo = (m: MeetingListItem): InfoTagProps => ({
  id: "meeting-form",
  text: meetingFormCaptions[m.meetingForm],
  icon: meetingFormIcon[m.meetingForm],
  details: compact([
    m.meetingLink && {
      id: "meeting-link",
      text: { translated: withoutProtocol(m.meetingLink) },
      icon: "video-camera",
      onOpen: [{ type: "load-url", url: m.meetingLink, newWindow: true }],
    },
    when(meetingPlaceCaption(m), (text) => ({
      id: "meeting-place",
      text,
      icon: "map-pin-area",
    })),
  ]),
});

const getDecisionTitle = (context: { lang: Language }, d: DecisionForMeeting): TMessage =>
  when(findLocalizedTextOrDefault(context, d.titles), (text) => ({ translated: text })) ??
  getDecisionName(d.decisionType);

export const getMeetingAgendaInfo = (
  m: MeetingListItem,
  lang: Language,
): InfoTagProps | undefined =>
  m.decisions.length
    ? {
        id: "matters",
        icon: "rows",
        text: { translated: String(m.decisions.length) },
        details: m.decisions.map((d, i) => ({
          id: String(i),
          icon: d.isInformational ? "chats" : "gavel",
          text: getDecisionTitle({ lang }, d),
        })),
      }
    : undefined;

export const getMeetingDocumentsInfo = (m: MeetingListItem): InfoTagProps | undefined =>
  m.documents.length
    ? {
        id: "documents",
        icon: "file-text",
        text: { translated: String(m.documents.length) },
        details: m.documents.map((d) => ({
          id: d.documentType,
          icon: "file-text",
          text: getDocumentLabel(d.documentType),
          tag: d.signedAt
            ? { color: "success", label: { key: "dbot.summary.signingStatus.signed" } }
            : d.sentAt
              ? { color: "info", label: { key: "dbot.summary.signingStatus.sent" } }
              : undefined,
        })),
      }
    : undefined;

export const prepMeetingItem = (
  state: ScheduleState,
  { companyName, lang }: { companyName: string; f: LocaleFormat; lang: Language },
  m: ScheduleMeeting,
): ScheduleItemProps => {
  return {
    company: companyName,
    date: m.meeting.meetingStartDate,
    time: m.meeting.meetingStartTime,
    icon: "calendar",
    key: m.id,
    statuses: state.accessType === AccessType.ADMIN ? prepItemStatus(m) : [],
    tags: compact([
      m.meeting.meetingNumber && {
        id: "meeting-number",
        text: { translated: m.meeting.meetingNumber },
        icon: "hash",
      },
      getMeetingFormInfo(m.meeting),
      getMeetingAgendaInfo(m.meeting, lang),
      getMeetingDocumentsInfo(m.meeting),
    ]),
    title: meetingTypeCaption(m.meeting),
    description: findMeetingDescription({ lang }, m.meeting),
    isSelected: state.selectedItem?.id === m.id,
    onSelect: openItemDetails(state, m.id),
  };
};

const reminderTitle = (m: ScheduleMeetingReminder): TMessage => {
  switch (m.meeting.meetingType) {
    case MeetingType.BOARD_MEETING:
      return { key: "schedule.reminder.bm.send-notice" };
    case MeetingType.GENERAL_MEETING:
      return m.meeting.isExtraordinary
        ? { key: "schedule.reminder.gm-extraordinary.send-notice" }
        : { key: "schedule.reminder.gm.send-notice" };
  }
};

const prepMeetingReminderItem = (
  state: ScheduleState,
  context: { currentDate: DateValue },
  m: ScheduleMeetingReminder,
): ScheduleItemProps => ({
  date: m.date,
  time: m.time,
  icon: m.isDone ? "check-circle" : "bell",
  iconColor: m.isDone ? "success" : undefined,
  key: m.id,
  title: reminderTitle(m),
  isSelected: state.selectedItem?.id === m.id,
  onSelect: openItemDetails(state, m.id),
  compact: true,
  statusTag: m.isDone
    ? { label: { key: "schedule.reminder.completed" }, color: "success" }
    : isAfter(context.currentDate, m.date)
      ? { label: { key: "schedule.reminder.overdue" }, color: "error" }
      : undefined,
});

const prepShareholderRegisterStatementReminderItem = (
  state: ScheduleState,
  context: { currentDate: DateValue },
  m: ShareholderRegisterStatementReminder,
): ScheduleItemProps => ({
  date: m.date,
  time: m.time,
  icon: m.isDone ? "check-circle" : "bell",
  iconColor: m.isDone ? "success" : undefined,
  key: m.id,
  title: { key: "schedule.reminder.shareholder-register-statement.submit" },
  isSelected: state.selectedItem?.id === m.id,
  onSelect: openItemDetails(state, m.id),
  compact: true,
  statusTag: m.isDone
    ? { label: { key: "schedule.reminder.completed" }, color: "success" }
    : isAfter(context.currentDate, m.date)
      ? { label: { key: "schedule.reminder.overdue" }, color: "error" }
      : undefined,
});

const prepBeneficialOwnersReminderItem = (
  state: ScheduleState,
  context: { currentDate: DateValue },
  m: BeneficialOwnersReminder,
): ScheduleItemProps => ({
  date: m.date,
  time: m.time,
  icon: m.isDone ? "check-circle" : "bell",
  iconColor: m.isDone ? "success" : undefined,
  key: m.id,
  title: { key: "schedule.reminder.beneficial-owners.submit" },
  isSelected: state.selectedItem?.id === m.id,
  onSelect: openItemDetails(state, m.id),
  compact: true,
  statusTag: m.isDone
    ? { label: { key: "schedule.reminder.completed" }, color: "success" }
    : isAfter(context.currentDate, m.date)
      ? { label: { key: "schedule.reminder.overdue" }, color: "error" }
      : undefined,
});

const prepItems = (
  userPrefs: UserPrefs["schedule"],
  state: ScheduleState,
  items: ScheduleItem[],
  context: {
    companyName: string;
    f: LocaleFormat;
    lang: Language;
    currentDate: DateValue;
    openItemDetails?: (state: ScheduleState, itemId: string, activeTab?: string) => DCEvent;
  },
): ScheduleItemProps[] =>
  sortItems(items, sortOrderOrDefault(userPrefs)).map<ScheduleItemProps>((m) => {
    switch (m.type) {
      case "meeting":
        return prepMeetingItem(state, context, m);
      case "meeting-reminder":
        return prepMeetingReminderItem(state, context, m);
      case "shareholder-register-statement-reminder":
        return prepShareholderRegisterStatementReminderItem(state, context, m);
      case "beneficial-owners-reminder":
        return prepBeneficialOwnersReminderItem(state, context, m);
    }
  });

const prepItemTypes = (state: ScheduleState): ScheduleViewProps["itemTypes"] => [
  {
    id: MeetingType.BOARD_MEETING,
    label: { key: "meetings.board-meetings" },
    selected: state.itemTypes.includes(MeetingType.BOARD_MEETING),
  },
  {
    id: MeetingType.GENERAL_MEETING,
    label: { key: "meetings.general-meetings" },
    selected: state.itemTypes.includes(MeetingType.GENERAL_MEETING),
  },
];

export const toScheduleRouteParams = (state: ScheduleState): ScheduleRouteParams => {
  return {
    accessType: state.accessType,
    orgNo: state.orgNo ?? "",
    view: state.view,
    year: String(toDate(getPeriodValue(state)).getFullYear()),
    month:
      getPeriodType(state) === "month" || state.view === "calendar"
        ? String(toDate(getPeriodValue(state)).getMonth() + 1)
        : undefined,
    selectedItem: state.selectedItem?.id,
    activeTab: state.selectedItem?.activeTab,
    shareholderId: state.shareholderId,
  };
};

export const toScheduleUrl = (state: ScheduleState) => scheduleUrl(toScheduleRouteParams(state));

const periodOptions: SchedulePeriodType[] = ["month", "year"];
const periodLabel = (item: SchedulePeriodType): TMessage => {
  switch (item) {
    case "year":
      return { key: "schedule.year" };
    case "month":
      return { key: "schedule.month" };
  }
};

const changePeriod = (state: ScheduleState, period: SchedulePeriod): DCEvent[] => [
  {
    type: "navigate",
    url: toScheduleUrl(
      state.view === "list"
        ? { ...state, listPeriod: period }
        : { ...state, calendarPeriod: period },
    ),
  },
];

const changePeriodType =
  (state: ScheduleState) =>
  (periodType: SchedulePeriodType): DCEvent[] =>
    changePeriod(state, { ...getPeriod(state), type: periodType });

const getYears = (context: { currentDate: DateValue }, state: ScheduleState) => {
  const currentYear = toDate(context.currentDate).getFullYear();
  const itemYears = state.items.map((item) => toDate(item.date).getFullYear());
  const minItemYear = min(itemYears) ?? currentYear;
  const maxItemYear = max(itemYears) ?? currentYear;
  const selectedYear = toDate(getPeriodValue(state)).getFullYear();
  const minYear = Math.min(minItemYear, selectedYear);
  const maxYear = Math.max(maxItemYear, selectedYear);
  return range(minYear - 1, maxYear + 2);
};

const toPeriodSelectorItems = (state: ScheduleState, years: number[]) =>
  getPeriodType(state) === "year"
    ? years.map((y) => String(y))
    : years.flatMap((y) => range(1, 13).map((m) => toYearMonthISOString(atMonth(String(y), m))));

const toPeriodSelectorValue = (state: ScheduleState, date?: DateValue): DateValue =>
  getPeriodType(state) === "year"
    ? toYearISOString(date ?? getPeriodValue(state))
    : toYearMonthISOString(date ?? getPeriodValue(state));

const formatPeriodSelectorValue = (state: ScheduleState, value?: DateValue): TMessageParam =>
  getPeriodType(state) === "year"
    ? { type: "year", value: value ?? getPeriodValue(state) }
    : { type: "capitalize", value: { type: "year-month", value: value ?? getPeriodValue(state) } };

export const prepPeriodSelector = (
  context: { currentDate: DateValue },
  state: ScheduleState,
): SelectProps<DateValue> => ({
  value: toPeriodSelectorValue(state),
  items: toPeriodSelectorItems(state, getYears(context, state)),
  idFn: identity,
  labelFn: (value) => formatPeriodSelectorValue(state, value),
  onSelect: (value) => changePeriod(state, { ...getPeriod(state), value }),
});

const prepPeriodTypeSelector = (
  state: ScheduleState,
): SelectProps<SchedulePeriodType> | undefined =>
  when(state.view === "list", () => ({
    items: periodOptions,
    value: getPeriodType(state),
    idFn: identity,
    labelFn: periodLabel,
    onSelect: changePeriodType(state),
  }));

export const movePeriod = (state: ScheduleState, amount: number): DCEvent[] =>
  changePeriod(state, {
    ...getPeriod(state),
    value:
      getPeriodType(state) === "year"
        ? plusYears(getPeriodValue(state), amount)
        : plusMonths(getPeriodValue(state), amount),
  });

const moveToToday = (state: ScheduleState): DCEvent[] =>
  changePeriod(state, { ...getPeriod(state), value: toLocalDateISOString(Date.now()) });

export const toView = (view?: string): "list" | "calendar" => (view === "calendar" ? view : "list");

export const changeView = (state: ScheduleState, view: "list" | "calendar"): DCEvent[] => [
  {
    type: "navigate",
    url: toScheduleUrl({
      ...state,
      listPeriod: { ...state.listPeriod, value: getPeriodValue(state) },
      calendarPeriod: { ...state.calendarPeriod, value: getPeriodValue(state) },
      view,
    }),
  },
];

const toggleItemType =
  (state: ScheduleState) =>
  (
    _: ChangeEvent<HTMLInputElement>,
    type: { id: ScheduleItemType; label: TMessage; selected: boolean },
    checked: boolean,
  ): DCEvent[] => [
    setInScheduleState(
      "itemTypes",
      checked ? uniq([...state.itemTypes, type.id]) : without(state.itemTypes, type.id),
    ),
  ];

const deleteMeeting = (item: ScheduleMeeting, ctrl: ProjectController) => {
  const meetingType = item.meeting.meetingType;
  const alert = (): AlertMessage | undefined => {
    switch (true) {
      case meetingType === MeetingType.BOARD_MEETING && ctrl.containsAnyGeneralMeetingDocuments:
        return {
          type: "warning",
          message: { key: "dbot.delete-project.also-delete-general-meeting-warning" },
        };
      case meetingType === MeetingType.GENERAL_MEETING && ctrl.containsAnyBoardMeetingDocuments:
        return {
          type: "warning",
          message: { key: "dbot.delete-project.also-delete-board-meeting-warning" },
        };
    }
  };
  ctrl.dispatch({
    type: "dbot/delete-project",
    alert: alert(),
    onSuccess: [
      withScheduleState((state) => [
        closeItemDetails(state),
        setInScheduleState(
          "items",
          reject(state.items, (x) => isMeetingType(x) && x.meeting.id === item.meeting.id),
        ),
        showAlert({ key: "dbot.delete-project-success" }, "success"),
      ]),
    ],
  });
};

const duplicateMeeting = (
  meetingType: MeetingType,
  { orgNo, projectId }: { orgNo: string; projectId: string },
): DCEvent => {
  return { type: "new-meeting", orgNo, projectId, meetingType };
};

export type ItemDetailsTab =
  | { name: "quick-edit" }
  | { name: "agenda" }
  | { name: "notice"; documentId: string }
  | { name: "minutes"; documentId: string };

const getDocumentTabs = (item: MeetingListItem): ItemDetailsTab[] =>
  compact(
    item.documents
      .filter((x) => {
        switch (x.documentType) {
          case DocumentType.notice_board_meeting:
          case DocumentType.board_meeting:
            return item.meetingType === MeetingType.BOARD_MEETING;
          case DocumentType.notice_general_meeting:
          case DocumentType.general_meeting:
            return item.meetingType === MeetingType.GENERAL_MEETING;
        }
      })
      .map<ItemDetailsTab | undefined>((x) => {
        switch (x.documentType) {
          case DocumentType.notice_board_meeting:
          case DocumentType.notice_general_meeting:
            return { name: "notice", documentId: x.projectDocumentId! };
          case DocumentType.board_meeting:
          case DocumentType.general_meeting:
            return { name: "minutes", documentId: x.projectDocumentId! };
        }
      }),
  ).sort((x) => (x.name === "notice" ? 0 : 1));

const getMeetingTabs = (
  item: MeetingListItem,
  { canEdit }: { canEdit: boolean },
): ItemDetailsTab[] =>
  compact([canEdit && { name: "quick-edit" }, { name: "agenda" }, ...getDocumentTabs(item)]);

const getTabLabel = (x: ItemDetailsTab, item: MeetingListItem): TMessage => {
  switch (x.name) {
    case "agenda":
      return { key: "schedule.agenda-n", params: { n: item.decisions.length } };
    case "quick-edit":
      return { key: "quick-edit" };
    case "notice":
      return { key: "meetings.notice" };
    case "minutes":
      return { key: "meetings.minutes" };
  }
};

const getTabTag = (x: ItemDetailsTab, item: MeetingListItem): TagProps | undefined => {
  switch (x.name) {
    case "notice":
      if (!item.documents.find((d) => d.documentId === x.documentId)?.sentAt) {
        return { color: "neutral", label: { key: "draft" } };
      }
      break;
  }
};

const getActiveTab = (
  state: SelectedItem,
  item: MeetingListItem,
  tabs: ItemDetailsTab[],
): ItemDetailsTab => {
  const active = tabs.find((x) => x.name === state.activeTab);

  switch (true) {
    case active !== undefined:
      return active;
    case item.meetingStartDate && isBefore(item.meetingStartDate, today()):
      return (
        tabs.find((x) => x.name === "minutes") ??
        tabs.find((x) => x.name === "notice") ??
        tabs.find((x) => x.name === "agenda") ??
        tabs[0]
      );
    default:
      return (
        tabs.find((x) => x.name === "notice") ?? tabs.find((x) => x.name === "agenda") ?? tabs[0]
      );
  }
};

const getDocumentIdFromTab = (tab: ItemDetailsTab): string | undefined => {
  switch (tab.name) {
    case "notice":
    case "minutes":
      return tab.documentId;
  }
};

const prepMeetingTabs = (
  state: ScheduleState,
  selectedItem: SelectedItem,
  item: MeetingListItem,
  tabs: ItemDetailsTab[],
  activeTab: ItemDetailsTab,
): TabProps[] =>
  tabs.map((tab) => ({
    id: tab.name,
    label: getTabLabel(tab, item),
    tag: getTabTag(tab, item),
    isActive: tab.name === activeTab.name,
    onSelect: [
      navigate(toScheduleUrl({ ...state, selectedItem: { ...selectedItem, activeTab: tab.name } })),
    ],
  }));

const loadComments = (projectId: string): DCEvent[] => [
  updateInScheduleState("selectedItem", (selectedItem) =>
    selectedItem ? { ...selectedItem, isLoadingComments: true } : undefined,
  ),
  commentsApi.getCommentsOnProject({
    projectId,
    onSuccess: (response, state) =>
      compact([
        state.schedule?.selectedItem &&
          setInScheduleState("selectedItem", {
            ...state.schedule?.selectedItem,
            isLoadingComments: false,
            comments: response.data.comments,
            editComment: {},
          }),
      ]),
    onFailure: () => [showGeneralError()],
  }),
];

const loadCommentsForDecision = (projectId: string, decisionId: string): DCEvent[] => [
  updateInScheduleState("selectedItem", (selectedItem) =>
    selectedItem ? { ...selectedItem, isLoadingComments: true } : undefined,
  ),
  commentsApi.getCommentsOnDecision({
    projectId,
    decisionId,
    onSuccess: (comments, state) =>
      compact([
        state.schedule?.selectedItem &&
          setInScheduleState("selectedItem", {
            ...state.schedule?.selectedItem,
            isLoadingComments: false,
            comments,
            editComment: {},
          }),
      ]),
    onFailure: () => [showGeneralError()],
  }),
];

const meetingTypeToNoticeType = (meetingType: MeetingType): DocumentType => {
  switch (meetingType) {
    case MeetingType.BOARD_MEETING:
      return DocumentType.notice_board_meeting;
    case MeetingType.GENERAL_MEETING:
      return DocumentType.notice_general_meeting;
  }
};

const getAttachmentsForDecision = (
  d: DecisionItem,
  meetingType: MeetingType,
): { id: string; filename: string }[] =>
  getDecisionAttachmentsInDocuments(d, [meetingTypeToNoticeType(meetingType)]);

const isExcludedInMeeting = (d: DecisionItem, meetingType: MeetingType): boolean =>
  meetingTypeToDocumentTypes(meetingType).every((dt) => isDecisionExcludedInDocument(d, dt));

const getDecisionsForMeeting = (
  context: { currentDecisions: DecisionItem[] },
  meetingType: MeetingType,
): DecisionItem[] => context.currentDecisions.filter((d) => !isExcludedInMeeting(d, meetingType));

const getMeetingDetailsPanel = (
  item: MeetingListItem,
  ctrl: ProjectController,
): DetailsPanelRef | undefined => {
  switch (item.meetingType) {
    case MeetingType.GENERAL_MEETING:
      return when(findNoticeToGeneralMeetingForm(ctrl.documents), (form) =>
        detailsPanelToRef(getNoticeToGeneralMeetingDetailsPanel(form)),
      );
  }
};

const getAllDocumentAttachments = (
  jsonData: ProjectJsonData,
  documentType: DocumentType,
): UploadedAttachment[] => {
  const d = jsonData.documents.find((d) => d.documentData.type === documentType);
  const documentAttachments = d?.attachments ?? [];
  const decisionAttachments: UploadedAttachment[] = jsonData.decisions.flatMap((d) =>
    d.attachments.filter((a) => a.selectedDocuments.includes(documentType)),
  );
  return documentAttachments.concat(decisionAttachments);
};

const hasNotice = (item: ScheduleMeeting) =>
  item.meeting.documents.some(
    (x) => x.documentType === meetingTypeToNoticeType(item.meeting.meetingType),
  );

const prepSelectedMeetingForEdit = (
  state: ScheduleState,
  { f, lang, orgNo }: { f: LocaleFormat; lang: Language; orgNo: string },
  selectedItem: SelectedItem,
  item: ScheduleMeeting,
  ctrl: ProjectController,
): ScheduleItemDetailsProps => {
  const decisions = getDecisionsForMeeting(ctrl.projectContext, item.meeting.meetingType);
  const tabs = getMeetingTabs(item.meeting, { canEdit: true });
  const activeTab = getActiveTab(selectedItem, item.meeting, tabs);

  const preppedAgendaItems =
    activeTab.name === "agenda"
      ? decisions.map<AgendaItemCardProps>((d) => {
          const attachments = getAttachmentsForDecision(d, item.meeting.meetingType);
          return {
            id: d.decisionId,
            title: d.title,
            description: when(findLocalizedTextOrDefault({ lang }, d.decisionTexts), (text) => ({
              type: "markdown",
              text,
            })),
            subtitleText: getMatterTypeLabel(d.decisionData),
            subtitleIcon: getDecisionIcon(d.decisionData),
            infoTags: compact<InfoTagProps>([
              attachments.length && {
                id: "attachments",
                text: { translated: String(attachments.length) },
                icon: "paperclip",
                detailsHeading: { key: "attachments" },
                details: attachments.map((a) => ({
                  id: a.id,
                  icon: "paperclip",
                  text: { translated: a.filename },
                  onOpen: () => ctrl.dispatch({ type: "dbot/open-attachment", attachmentId: a.id }),
                })),
              },
            ]),
            onOpen: [
              setInScheduleState("selectedItem", {
                ...selectedItem,
                selectedAgendaItem: d.decisionId,
              }),
              ...loadCommentsForDecision(item.meeting.projectId, d.decisionId),
            ],
            onClose: [
              setInScheduleState("selectedItem", {
                ...selectedItem,
                selectedAgendaItem: undefined,
              }),
              ...loadComments(item.meeting.projectId),
            ],
            onEdit: () =>
              ctrl.dispatch({
                type: "dbot/open-details-panel",
                panel: { type: "decision", decisionId: d.decisionId },
              }),
          };
        })
      : undefined;

  const descriptionModel = (): LocalizedTextEditorModel | undefined => {
    switch (item.meeting.meetingType) {
      case MeetingType.BOARD_MEETING:
        return ctrl.boardMeetingFields.noticeDescription;
      case MeetingType.GENERAL_MEETING:
        return ctrl.documents
          .map((x) => x.form)
          .find((x) => x.type === DocumentType.notice_general_meeting)?.additionalDetailsTextModel;
    }
  };

  const activeDocument = ctrl.documents.find(
    (x) => x.documentId === getDocumentIdFromTab(activeTab),
  );
  const activeDocumentAttachments = when(activeDocument, (d) =>
    getAllDocumentAttachments(ctrl.projectContext.jsonData, d.type),
  );
  const activeTabContent = ctrl.documentTabs.find(
    (x) => x.documentId === getDocumentIdFromTab(activeTab),
  )?.content;
  logDebug(
    "Schedule: activeTab",
    toJS(activeTab),
    "documentIdactiveTabContent",
    toJS(activeTabContent),
  );

  return {
    type: "meeting-details",
    meetingType: item.meeting.meetingType,
    toolbar: compact([
      {
        key: "delete",
        variant: "icon",
        icon: "trash",
        tooltip: { key: "delete" },
        onClick: () => deleteMeeting(item, ctrl),
      },
      {
        key: "duplicate",
        variant: "icon",
        icon: "copy",
        tooltip: { key: "duplicate" },
        onClick: [duplicateMeeting(item.meeting.meetingType, ctrl)],
      },
      hasNotice(item) && {
        key: "preview-meeting",
        variant: "icon",
        icon: "arrow-square-out",
        tooltip: { key: "schedule.open-meeting" },
        onClick: [
          item.meeting.meetingType === MeetingType.GENERAL_MEETING
            ? {
                type: "load-url",
                newWindow: true,
                url: previewGeneralMeetingForAdmin(ctrl.orgNo, ctrl.projectId),
              }
            : {
                type: "load-url",
                newWindow: true,
                url: previewBoardMeetingForAdmin(ctrl.orgNo, ctrl.projectId),
              },
        ],
      },
      {
        key: "close",
        variant: "icon",
        icon: "x",
        tooltip: { key: "close" },
        color: "inherit",
        onClick: [closeItemDetails(state)],
      },
    ]),
    tabs: prepMeetingTabs(state, selectedItem, item.meeting, tabs, activeTab),
    activeTab: activeTab.name,
    activeTabContent,
    activeTabAttachments: activeDocumentAttachments,
    ctrl,
    title: meetingTypeCaption(item.meeting),
    date: f.formatDate(item.meeting.meetingStartDate, { relative: true }),
    openAttachment: (id, attachments) =>
      openAttachment({ id, attachments, projectId: item.meeting.projectId }),
    removeDocument: (documentId) => ctrl.dispatch(removeDocumentAction(documentId)),
    editAllSettings: when(
      getMeetingDetailsPanel(item.meeting, ctrl),
      (panel) => () => ctrl.dispatch({ type: "dbot/open-details-panel", panel }),
    ),
    isDescriptionEnabled: selectedItem.isDescriptionEnabled ?? !descriptionModel()?.isEmpty,
    enableDescription: [
      setInScheduleState("selectedItem", { ...selectedItem, isDescriptionEnabled: true }),
    ],
    agendaItems: preppedAgendaItems,
    selectedAgendaItem: preppedAgendaItems?.find((x) => x.id === selectedItem.selectedAgendaItem),
    comments: prepComments(selectedItem, ctrl.dispatch),
    downloadDocument: (content) =>
      isMeetingType(item)
        ? prepDownloadDocument(
            { orgNo },
            item,
            content.documentId,
            !isEmpty(activeDocumentAttachments),
          )
        : undefined,
    showDocumentStatus: prepShowDocumentStatus(selectedItem, activeTab),
    showPlaceholderSettings: showMeetingPlaceholderSettings(item),
    openProject: [
      navigateToProjectEvent(
        { orgNo: ctrl.orgNo, projectId: ctrl.projectId },
        {
          type: "project-navigation-state",
          from: toScheduleUrl(state),
          overlay: true,
          documentId: getDocumentIdFromTab(activeTab),
        },
      ),
    ],
  };
};

const prepSelectedMeetingReminder = (
  state: ScheduleState,
  context: { currentDate: DateValue; orgNo: string },
  item: ScheduleMeetingReminder,
): ScheduleItemDetailsProps => {
  return {
    type: "meeting-reminder-details",
    date: item.date,
    title: reminderTitle(item),
    status: item.isDone
      ? "completed"
      : isAfter(context.currentDate, item.date)
        ? "overdue"
        : "not-completed",
    openProject: [
      navigateToProjectEvent(
        { orgNo: context.orgNo, projectId: item.meeting.projectId },
        {
          type: "project-navigation-state",
          from: toScheduleUrl(state),
          overlay: true,
          documentId: item.meeting.documents.find(
            (d) => d.documentType === DocumentType.notice_general_meeting,
          )?.documentId,
        },
      ),
    ],
    close: [closeItemDetails(state)],
  };
};

const prepShareholderRegisterStatementReminder = (
  state: ScheduleState,
  context: { currentDate: DateValue; orgNo: string },
  item: ShareholderRegisterStatementReminder,
): ScheduleItemDetailsProps => {
  return {
    type: "shareholder-register-statement-reminder-details",
    title: { key: "schedule.reminder.shareholder-register-statement.submit" },
    date: item.date,
    status: item.isDone
      ? "completed"
      : isAfter(context.currentDate, item.date)
        ? "overdue"
        : "not-completed",
    close: [closeItemDetails(state)],
    openShareholderRegisterStatement: state.orgNo
      ? [navigate(adminShareholderStatement(state.orgNo))]
      : [],
    reportingYear: item.reportingYear,
  };
};

const prepBeneficialOwnersReminder = (
  state: ScheduleState,
  context: { currentDate: DateValue; orgNo: string },
  item: BeneficialOwnersReminder,
): ScheduleItemDetailsProps => {
  return {
    type: "beneficial-owners-reminder-details",
    title: { key: "schedule.reminder.beneficial-owners.submit" },
    date: item.date,
    status: item.isDone
      ? "completed"
      : isAfter(context.currentDate, item.date)
        ? "overdue"
        : "not-completed",
    close: [closeItemDetails(state)],
    openBeneficialOwnersPage: state.orgNo ? [navigate(adminBeneficialOwners(state.orgNo))] : [],
  };
};

const prepMeetingPlaceholderDetails = (
  state: ScheduleState,
  { lang, companyName }: { f: LocaleFormat; lang: Language; companyName: string },
  item: ScheduleMeeting,
): MeetingPlaceholderDetailsProps => ({
  type: "meeting-placeholder-details",
  addToCalendar: undefined, // TODO backend must provide ical in listing
  close: [closeItemDetails(state)],
  date: item.date,
  description: findMeetingDescription({ lang }, item.meeting),
  endTime: item.endTime,
  helpText: { key: "schedule.meeting-placeholder-help-text" },
  infoTags: [getMeetingFormInfo(item.meeting)],
  infoTagDetails: getMeetingFormInfo(item.meeting).details,
  startTime: item.time,
  title: {
    key: "meeting_at",
    params: { meeting: meetingTypeCaption(item.meeting), place: companyName },
  },
});

const openAttachment = ({
  id,
  attachments,
  projectId,
}: {
  id: string;
  attachments: UploadedAttachment[];
  projectId: string;
}): DCEvent[] =>
  compact([
    when(
      attachments.find((a) => a.id === id),
      (attachment) => ({
        type: "preview-file",
        filename: attachment.filename,
        file: {
          url: projectApi.downloadAttachmentUrl({
            projectId: projectId,
            attachmentId: attachment.id,
          }),
        },
      }),
    ),
  ]);

const prepDownloadDocument = (
  context: { orgNo: string },
  item: ScheduleMeeting | ScheduleMeetingReminder,
  documentId: string,
  hasAttachments: boolean,
): DownloadAsPdfButtonProps | undefined =>
  when(
    item.meeting.documents.find((x) => x.documentId === documentId),
    (d) => ({
      documentType: d.documentType,
      onDownload: downloadEvent({ url: `/document/view/pdf/${d.documentId}/${context.orgNo}` }),
      onDownloadWithAttachments: hasAttachments
        ? downloadEvent({
            // TODO
            url: `/boardmember-view/preview-meeting/download-notice-and-attachments/${d.documentId}`,
          })
        : undefined,
    }),
  );

const prepShowDocumentStatus = (
  selectedItem: SelectedItem,
  activeTab: ItemDetailsTab,
): LinkButtonProps | undefined => {
  const ctrl = selectedItem.ctrl;
  const context = ctrl?.projectContext;
  const documentId = "documentId" in activeTab ? activeTab.documentId : undefined;
  const documentItem = documentId
    ? context?.currentDocuments.find((d) => d.documentId === documentId)
    : undefined;
  const documentStatus = context && documentId ? getDocumentStatus(context, documentId) : undefined;

  switch (activeTab.name) {
    case "notice":
      return when(
        documentItem && openViewingStatusDialog(documentItem, documentStatus),
        (action) => ({
          label: action.label,
          onClick: () => ctrl?.dispatch([action]),
        }),
      );

    case "minutes":
      return when(
        documentItem && openSigningStatusDialogAction(documentItem, documentStatus),
        (action) => ({
          label: action.label,
          onClick: () => ctrl?.dispatch([action]),
        }),
      );

    default:
      return undefined;
  }
};

const prepSelectedMeetingForView = (
  state: ScheduleState,
  context: {
    f: LocaleFormat;
    lang: Language;
    currentDate: DateValue;
    orgNo: string;
    companyName: string;
    dispatch: EventDispatcher;
  },
  selectedItem: SelectedItem,
  item: ScheduleMeeting,
  itemDetails: GetScheduleItemDetailsResponse,
): ScheduleItemDetailsProps => {
  const tabs = getMeetingTabs(item.meeting, { canEdit: false }).filter((x) => {
    switch (x.name) {
      case "notice":
        return itemDetails.notice !== undefined;
      case "minutes":
        return itemDetails.minutes !== undefined;
      default:
        return true;
    }
  });
  const activeTab = getActiveTab(selectedItem, item.meeting, tabs);
  const preppedAgendaItems: AgendaItemCardProps[] = item.meeting.decisions.map((x) => ({
    id: x.decisionId,
    title: getDecisionTitle(context, x),
    description: when(findLocalizedTextOrDefault(context, x.descriptions), (text) => ({
      type: "markdown",
      text,
    })),
    subtitleText: x.isInformational
      ? { key: "dbot.information-matter" }
      : { key: "dbot.decision-matter" },
    subtitleIcon: x.isInformational ? "chats" : "gavel",
    infoTags: compact<InfoTagProps>([
      x.attachments?.length && {
        id: "attachments",
        text: { translated: String(x.attachments.length) },
        icon: "paperclip",
        detailsHeading: { key: "attachments" },
        details: x.attachments.map((a) => ({
          id: a.id,
          icon: "paperclip",
          text: { translated: a.filename },
          onOpen: openAttachment({
            id: a.id,
            attachments: x.attachments ?? [],
            projectId: item.meeting.projectId,
          }),
        })),
      },
    ]),
    onOpen: [
      setInScheduleState("selectedItem", {
        ...selectedItem,
        selectedAgendaItem: x.decisionId,
      }),
      ...loadCommentsForDecision(item.meeting.projectId, x.decisionId),
    ],
    onClose: [
      setInScheduleState("selectedItem", {
        ...selectedItem,
        selectedAgendaItem: undefined,
      }),
      ...loadComments(item.meeting.projectId),
    ],
  }));
  const activeTabAttachments =
    activeTab.name === "notice"
      ? itemDetails.notice?.attachments
      : activeTab.name === "minutes"
        ? itemDetails.minutes?.attachments
        : undefined;
  return {
    type: "meeting-details",
    meetingType: item.meeting.meetingType,
    title: meetingTypeCaption(item.meeting),
    date: context.f.formatDateAndTime(item.date, { startTime: item.time, endTime: item.endTime }),
    toolbar: compact([
      hasNotice(item) &&
        item.meeting.meetingType === MeetingType.BOARD_MEETING && {
          key: "open-meeting",
          variant: "secondary",
          label: { key: "schedule.open-meeting" },
          onClick: [
            {
              type: "navigate",
              url: boardMemberBoardMeetingPage(context.orgNo, item.meeting.projectId),
              state: {
                type: "meeting-navigation-state",
                from: "schedule",
                params: toScheduleRouteParams(state),
              },
            } satisfies NavigateEvent<MeetingNavigationState>,
          ],
        },
      {
        key: "close",
        variant: "icon",
        icon: "x",
        tooltip: { key: "close" },
        color: "inherit",
        onClick: [closeItemDetails(state)],
      },
    ]),
    tabs: prepMeetingTabs(state, selectedItem, item.meeting, tabs, activeTab),
    activeTab: activeTab.name,
    activeTabContent:
      activeTab.name === "notice" && itemDetails.notice?.content
        ? [
            {
              type: "document",
              documentId: itemDetails.notice.id,
              content: itemDetails.notice.content,
            },
          ]
        : activeTab.name === "minutes" && itemDetails.minutes?.content
          ? [
              {
                type: "document",
                documentId: itemDetails.minutes.id,
                content: itemDetails.minutes.content,
              },
            ]
          : undefined,
    activeTabAttachments: activeTabAttachments,
    agendaItems: preppedAgendaItems,
    selectedAgendaItem: preppedAgendaItems?.find((x) => x.id === selectedItem.selectedAgendaItem),
    comments: prepComments(selectedItem, context.dispatch),
    openAttachment: (id, attachments) =>
      openAttachment({ id, attachments, projectId: item.meeting.projectId }),
    downloadDocument: (content) =>
      prepDownloadDocument(context, item, content.documentId, !isEmpty(activeTabAttachments)),
  };
};

const prepSelectedItem = (
  state: ScheduleState,
  context: {
    f: LocaleFormat;
    lang: Language;
    currentDate: DateValue;
    orgNo: string;
    companyName: string;
    dispatch: EventDispatcher;
  },
): ScheduleItemDetailsProps | undefined => {
  const selectedItem = state.selectedItem;
  const item = state.items.find((item) => item.id === selectedItem?.id)!;
  if (!selectedItem || !item) {
    return;
  }

  switch (item.type) {
    case "meeting":
      switch (true) {
        case state.accessType === AccessType.ADMIN && !selectedItem.ctrl:
          return loading;
        case state.accessType === AccessType.ADMIN:
          return prepSelectedMeetingForEdit(state, context, selectedItem, item, selectedItem.ctrl!);
        case !isMeetingPlaceholder(item) && !selectedItem.itemDetails:
          return loading;
        case !isMeetingPlaceholder(item):
          return prepSelectedMeetingForView(
            state,
            context,
            selectedItem,
            item,
            selectedItem.itemDetails!,
          );
        default:
          return prepMeetingPlaceholderDetails(state, context, item);
      }
    case "meeting-reminder":
      return prepSelectedMeetingReminder(state, context, item);
    case "shareholder-register-statement-reminder":
      return prepShareholderRegisterStatementReminder(state, context, item);
    case "beneficial-owners-reminder":
      return prepBeneficialOwnersReminder(state, context, item);
  }
};

const prepComments = (
  selectedItem: SelectedItem,
  dispatch: EventDispatcher,
): CommentListProps | undefined => {
  const projectId = selectedItem.projectId;
  if (!selectedItem.comments) {
    return undefined;
  }

  return {
    decisionId: selectedItem.selectedAgendaItem,
    comments: selectedItem.comments,
    editState: selectedItem.editComment,
    isLoading: selectedItem.isLoadingComments,
    actions: {
      onEditComment: (comment) =>
        dispatch([
          setInScheduleState("selectedItem", {
            ...selectedItem,
            editComment: {
              updateCommentId: comment?.id,
              commentText: comment?.commentText,
              showEditor: comment !== undefined,
            },
          }),
        ]),

      onDeleteComment: (deleteCommentId) =>
        dispatch([
          setInScheduleState("selectedItem", {
            ...selectedItem,
            editComment: {
              ...selectedItem.editComment,
              deleteCommentId,
            },
          }),
        ]),

      onConfirmDelete: (deletedCommentId) =>
        dispatch(deleteComment(deletedCommentId, projectId, selectedItem.selectedAgendaItem)),
      onSave: (decisionId) =>
        dispatch(
          addOrEditComment({
            commentId: selectedItem.editComment?.updateCommentId,
            decisionId,
            commentText: selectedItem.editComment?.commentText,
            projectId,
          }),
        ),
      onChange: (commentText) =>
        dispatch([
          setInScheduleState("selectedItem", {
            ...selectedItem,
            editComment: {
              ...selectedItem.editComment,
              commentText,
            },
          }),
        ]),
      onCancel: () =>
        dispatch([
          setInScheduleState("selectedItem", {
            ...selectedItem,
            editComment: {},
          }),
        ]),
      onToggleEditor: () =>
        dispatch([
          setInScheduleState("selectedItem", {
            ...selectedItem,
            editComment: {
              ...selectedItem.editComment,
              showEditor: !selectedItem.editComment?.showEditor,
            },
          }),
        ]),
    },
  };
};

const addOrEditComment = ({
  commentId,
  decisionId,
  commentText,
  projectId,
}: {
  commentId?: string;
  decisionId?: string;
  commentText?: string;
  projectId?: string;
}): DCEvent[] => {
  const onSuccess = () =>
    decisionId ? loadCommentsForDecision(projectId!, decisionId) : loadComments(projectId!);
  return commentId !== undefined
    ? compact([
        commentId &&
          commentText &&
          commentsApi.updateComment({
            data: {
              commentId,
              commentText,
            },
            onSuccess,
            onFailure: () => [showGeneralError()],
          }),
      ])
    : compact([
        projectId &&
          commentText &&
          commentsApi.addComment({
            data: {
              projectId,
              decisionId,
              commentText,
            },
            onSuccess,
            onFailure: () => [showGeneralError()],
          }),
      ]);
};

const deleteComment = (commentId?: string, projectId?: string, decisionId?: string): DCEvent[] =>
  compact([
    commentId &&
      commentsApi.deleteComment({
        data: { commentId },
        onSuccess: () =>
          decisionId
            ? loadCommentsForDecision(projectId!, decisionId)
            : loadComments(projectId ?? ""),
        onFailure: () => [showGeneralError()],
      }),
  ]);

const prepSortOrder = (
  state: ScheduleState,
  userPrefs: UserPrefs["schedule"],
): { label: TMessage; icon: IconName; onClick: DCEvent[] } | undefined => {
  if (state.view === "calendar") {
    return undefined;
  }
  const order = sortOrderOrDefault(userPrefs);
  return {
    icon: order === "asc" ? "sort-ascending" : "sort-descending",
    label: {
      key:
        userPrefs?.sortOrder === "asc"
          ? "schedule.sort-by-date-descending"
          : "schedule.sort-by-date-ascending",
    },
    onClick: [
      ...updateUserPrefEvent("schedule", {
        ...userPrefs,
        sortOrder: order === "asc" ? "desc" : "asc",
      }),
    ],
  };
};

export const defaultMeetingDate = (
  context: { currentDate: DateValue },
  state: ScheduleState,
): DateValue => {
  switch (getPeriodType(state)) {
    case "month":
      if (isSameMonth(context.currentDate, getPeriodValue(state))) {
        return context.currentDate;
      }
      return getPeriodValue(state);
    case "year":
      if (isSameYear(context.currentDate, getPeriodValue(state))) {
        return context.currentDate;
      }
      return getPeriodValue(state);
  }
};

export const prepScheduleProps = ({
  appBar,
  alertStore,
  company,
  f,
  t,
  lang,
  state = defaultScheduleState(),
  currentDate,
  dispatch,
  userPrefs,
}: {
  appBar: AppBarData | null;
  alertStore: AlertMessageStore;
  company: Company;
  f: LocaleFormat;
  t: TFunction;
  lang: Language;
  state?: ScheduleState;
  currentDate: DateValue;
  dispatch: EventDispatcher;
  userPrefs: UserPrefs["schedule"];
}): ScheduleViewProps => {
  const orgNo = state.orgNo ?? "";
  const filteredItems = filterItems(state);
  const preppedItems = prepItems(userPrefs, state, filteredItems, {
    companyName: company.name,
    f,
    lang,
    currentDate,
  });

  const list: ToggleButtonProps & { onClick: DCEvent[] } = {
    variant: "basic",
    icon: "rows",
    tooltip: { key: "schedule.view.list" },
    selected: state.view === "list",
    onClick: changeView(state, "list"),
  };
  const calendar: ToggleButtonProps & { onClick: DCEvent[] } = {
    variant: "basic",
    icon: "calendar-dots",
    tooltip: { key: "schedule.view.calendar" },
    selected: state.view === "calendar",
    onClick: changeView(state, "calendar"),
  };
  const addItems =
    state.accessType === AccessType.ADMIN
      ? newMeetingMenuItems(
          state,
          { t, currentDate },
          { date: defaultMeetingDate({ currentDate }, state) },
        )
      : undefined;
  const addLabel: TMessage = { key: "meetings.new-meeting" };
  return {
    addItems,
    addLabel,
    appBar,
    alertStore,
    itemTypes: prepItemTypes(state),
    onNextPeriod: movePeriod(state, 1),
    onPrevPeriod: movePeriod(state, -1),
    onToday: moveToToday(state),
    onToggleItemType: toggleItemType(state),
    periodSelector: prepPeriodSelector({ currentDate }, state),
    periodTypeSelector: prepPeriodTypeSelector(state),
    selectedItem: prepSelectedItem(state, {
      f,
      lang,
      currentDate,
      orgNo,
      companyName: company.name,
      dispatch,
    }),
    sortOrder: prepSortOrder(state, userPrefs),
    viewOptions: [list, calendar],
    view:
      state.view === "list"
        ? {
            type: "list",
            emptyState: !filteredItems.length
              ? {
                  illustration: "calendar",
                  title: {
                    key: "schedule.empty-state.title",
                    params: { date: formatPeriodSelectorValue(state) },
                  },
                  actions: compact([
                    addItems && {
                      type: "dropdown",
                      key: "create-meeting",
                      props: {
                        button: {
                          type: "button",
                          variant: "secondary",
                          label: addLabel,
                          icon: "plus",
                        },
                        items: addItems,
                      },
                    },
                  ]),
                }
              : undefined,
            items: preppedItems,
            isLoading: state.isLoading,
          }
        : {
            type: "calendar",
            calendar: makeCalendar({
              month: getPeriodValue(state),
              today: currentDate,
              selected: getPeriodValue(state),
              events: preppedItems.filter((item) => item.date),
              eventDate: (item) => item.date!,
            }),
            addItems:
              state.accessType === AccessType.ADMIN
                ? (day) =>
                    newMeetingMenuItems(
                      state,
                      {
                        t,
                        currentDate,
                      },
                      { date: day.date },
                    )
                : undefined,
            isLoading: state.isLoading,
          },
  };
};
